/**
 * In-flight training or meal plan create that outlives the modal.
 * Closing the overlay does not abort the request.
 */

const listeners = new Set();

/** @type {{ training: object | null, meal: object | null }} */
let jobs = { training: null, meal: null };

export function getBackgroundCreateJobs() {
  return jobs;
}

export function subscribeBackgroundCreateJobs(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function emit() {
  const snapshot = jobs;
  listeners.forEach((listener) => {
    listener(snapshot);
  });
}

const expandListeners = new Set();

/**
 * Page that owns the progress UI. Return true when this page opened it.
 * @param {(kind: 'training' | 'meal') => boolean} listener
 */
export function subscribeBackgroundCreateExpand(listener) {
  expandListeners.add(listener);
  return () => expandListeners.delete(listener);
}

/**
 * @param {'training' | 'meal'} kind
 * @returns {boolean} true when a mounted page opened its progress UI
 */
export function requestBackgroundCreateExpand(kind) {
  if (kind !== 'training' && kind !== 'meal') return false;
  let handled = false;
  expandListeners.forEach((listener) => {
    if (listener(kind) === true) handled = true;
  });
  return handled;
}

/**
 * @param {number} startedAt
 * @param {number} now
 * @returns {string}
 */
export function formatCreateJobElapsed(startedAt, now) {
  if (!Number.isFinite(startedAt) || !Number.isFinite(now)) return '';
  const seconds = Math.max(0, Math.floor((now - startedAt) / 1000));
  if (seconds < 60) return `${seconds}s elapsed`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}m ${remainder}s elapsed`;
}

/**
 * @param {'training' | 'meal'} kind
 * @param {object | null} next
 */
export function setBackgroundCreateJob(kind, next) {
  if (kind !== 'training' && kind !== 'meal') return;
  jobs = { ...jobs, [kind]: next };
  emit();
}

export function clearBackgroundCreateJob(kind) {
  setBackgroundCreateJob(kind, null);
}

/**
 * The in-page progress UI unmounted or cannot show. The dock must become visible.
 * @param {'training' | 'meal'} kind
 */
export function hideBackgroundCreateProgress(kind) {
  if (kind !== 'training' && kind !== 'meal') return;
  const current = getBackgroundCreateJobs()[kind];
  if (!current || current.status !== 'running' || !current.progressVisible) return;
  setBackgroundCreateJob(kind, { ...current, progressVisible: false });
}

/**
 * Meal create finished. Keep the result in the open modal when the page is
 * still mounted. If the rider left Nutrition, or closed the modal, the shell
 * banner has to carry ready/error.
 *
 * @param {{ mounted: boolean, modalOpen: boolean, status: string, message?: string }} input
 * @returns {{ status: string, message?: string } | null} null means clear the job
 */
export function mealCreateJobAfterFinish(input) {
  if (!input || input.status === 'clear') return null;
  if (input.mounted && input.modalOpen) return null;
  return { status: input.status, message: input.message };
}
